"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.REPORT_REVIEW_STALLED_MS = exports.REPORT_REVIEW_SCHEMA_VERSION = void 0;
exports.reportReviewDocumentId = reportReviewDocumentId;
exports.buildReportReviewSource = buildReportReviewSource;
exports.isReportReviewStale = isReportReviewStale;
exports.checkReportFacts = checkReportFacts;
const defect_replacements_1 = require("./defect-replacements");
const media_1 = require("./media");
exports.REPORT_REVIEW_SCHEMA_VERSION = 1;
exports.REPORT_REVIEW_STALLED_MS = 60_000;
function reportReviewDocumentId(reportType, reportId) {
    return `${reportType}--${reportId}`;
}
function buildReportReviewSource(input) {
    return {
        customerNamePresent: Boolean(input.customerName.trim()),
        customerAddressPresent: Boolean(input.customerAddress.trim()),
        // Source equality is deterministic and excludes generic prose and expiring URLs.
        // Review-relevant facts and custom text invalidate a completed review.
        blocks: [...input.blocks].sort((a, b) => a.order - b.order).map((block) => {
            const defectRef = block.defectRef?.replace(/^wp:/, 'wp_') ?? '';
            const defect = input.defects.get(defectRef) ?? input.defects.get(block.defectRef ?? '');
            const content = block.customContent || defect?.content || '';
            const replacements = Object.fromEntries(Object.entries(block.replacements ?? {}).map(([key, value]) => [key, value.trim()]));
            const rendered = (0, defect_replacements_1.applyDefectReplacements)(content, replacements);
            return {
                id: block.id, type: block.type, defectRef,
                locationId: block.locationId === 'unassigned' ? '' : block.locationId ?? '', findingId: block.findingId ?? '',
                places: [...new Set(block.places)].sort(),
                customPlaceText: block.customPlaceText?.trim() ?? '',
                amount: block.amount, price: block.price ?? null,
                hasContent: Boolean(content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()),
                missingVariables: (0, defect_replacements_1.extractDefectReplacementSlots)(rendered).map((slot) => slot.key),
                replacements: Object.entries(block.replacements ?? {})
                    .filter(([key]) => !key.startsWith('_')).sort(([a], [b]) => a.localeCompare(b))
                    .map(([key, value]) => ({ key, value })),
                mediaIds: (block.images ?? []).map(media_1.normalizeMediaReference)
                    .filter(media_1.isMediaReady)
                    .map((image) => `${image.provider}:${image.id}`).sort(),
                needsReview: block.needsReview === true,
                ...(block.type === 'defect' && (block.isCustomDefect || !defectRef) && block.customContent?.trim()
                    ? { customContent: block.customContent } : {}),
            };
        }),
    };
}
function isReportReviewStale(review, source) {
    return JSON.stringify(review.source) !== JSON.stringify(source);
}
function checkReportFacts(source) {
    const issues = [];
    const add = (check, severity, title, description, targetLabel, blockId = null) => {
        issues.push({ id: `${check}:${blockId ?? 'customer'}`, check, severity, title,
            description, targetLabel, blockId, state: 'open' });
    };
    if (!source.customerNamePresent)
        add('missing_customer_name', 'critical', 'חסר שם לקוח', 'יש להשלים את שם הלקוח בפרטי הדוח.', 'פרטי לקוח');
    if (!source.customerAddressPresent)
        add('missing_customer_address', 'critical', 'חסרה כתובת הנכס', 'יש להשלים את כתובת הנכס בפרטי הדוח.', 'פרטי לקוח');
    const evidence = new Set();
    let activeFinding = '';
    // Galleries and follow-up blocks can own the evidence, not only the primary
    // defect. Respect explicit ownership; legacy adjacency ends at the next room.
    for (const block of source.blocks) {
        if (block.type === 'location')
            activeFinding = '';
        if (block.type === 'defect')
            activeFinding = block.findingId || block.id;
        const owner = block.findingId || activeFinding;
        if (owner && block.mediaIds.length)
            evidence.add(owner);
    }
    const duplicates = new Map();
    let activeLocation = '';
    let findingIndex = 0;
    for (const [index, block] of source.blocks.entries()) {
        if (block.type === 'location')
            activeLocation = block.locationId || block.places.join(',') || block.customPlaceText;
        const isFinding = block.type === 'defect';
        if (isFinding)
            findingIndex += 1;
        const label = isFinding ? `ממצא ${findingIndex}` : `בלוק ${index + 1}`;
        if (block.missingVariables.length)
            add('unfilled_variables', 'critical', 'פרטים משתנים לא הושלמו', `יש להשלים את השדות: ${block.missingVariables.join(', ')}. הנוסח הכללי עצמו אינו נבדק.`, label, block.id);
        if (!isFinding)
            continue;
        const location = block.locationId || block.places.join(',') || block.customPlaceText || activeLocation;
        if (!block.hasContent)
            add('missing_content', 'critical', 'אין תוכן זמין לממצא', 'יש לבחור ממצא מהספרייה או להשלים תוכן לממצא הידני.', label, block.id);
        if (!location)
            add('missing_place', 'warning', 'לא תועד מיקום לממצא', 'יש לציין היכן הממצא נמצא. זו הערה על התיעוד, לא על כיסוי הבדיקה.', label, block.id);
        if (!evidence.has(block.findingId || block.id))
            add('missing_photo', 'warning', 'לא תועדה תמונה לממצא', 'לא נמצאה תמונה בממצא או בגלריה המשויכת אליו. בדוק אם נדרש תיעוד חזותי.', label, block.id);
        if (block.needsReview)
            add('pending_inspector_review', 'warning', 'הממצא ממתין לבדיקת הבודק', 'יש לבדוק את הבחירה ולאשר אותה בעורך הממצא.', label, block.id);
        if (!Number.isFinite(block.amount) || block.amount <= 0)
            add('invalid_quantity', 'critical', 'כמות הממצא אינה תקינה', 'הכמות חייבת להיות מספר גדול מאפס.', label, block.id);
        if (block.price !== null && (!Number.isFinite(block.price) || block.price < 0))
            add('invalid_price', 'critical', 'מחיר הממצא אינו תקין', 'מחיר שהוזן חייב להיות מספר שאינו שלילי. אין כאן הערכת מחיר AI.', label, block.id);
        if (block.defectRef && location) {
            const key = JSON.stringify([block.defectRef, location, block.replacements, block.amount, block.price]);
            const original = duplicates.get(key);
            if (original)
                add('duplicate_finding', 'warning', 'בחירה כפולה באותו מיקום', `אותו ממצא מהספרייה נבחר גם ב${original}, עם אותם פרטים וכמות. בדוק אם הכפילות מכוונת.`, label, block.id);
            else
                duplicates.set(key, label);
        }
    }
    // Stable severity ordering preserves source order within each group.
    const severityOrder = new Map([['critical', 0], ['warning', 1], ['suggestion', 2]]);
    return issues.sort((a, b) => (severityOrder.get(a.severity) ?? 0) - (severityOrder.get(b.severity) ?? 0));
}
